import type { ElementTransformer, Transformer } from "@lexical/markdown"
import {
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  CHECK_LIST,
  HEADING,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  ORDERED_LIST,
  QUOTE,
  STRIKETHROUGH,
  TEXT_MATCH_TRANSFORMERS,
  UNORDERED_LIST,
} from "@lexical/markdown"
import {
  $createTableCellNode,
  $createTableNode,
  $createTableRowNode,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableRowNode,
} from "@lexical/table"
import type { ElementNode, LexicalNode } from "lexical"
import {
  $createParagraphNode,
  $createTextNode,
  $isElementNode,
  $isParagraphNode,
  $isTextNode,
} from "lexical"

// Very primitive table setup
const TABLE_ROW_REG_EXP = /^(?:\|)(.+)(?:\|)\s?$/

export const TABLE: ElementTransformer = {
  dependencies: [TableNode, TableRowNode, TableCellNode],
  export: (
    node: LexicalNode,
    exportChildren: (elementNode: ElementNode) => string,
  ) => {
    if (!$isTableNode(node)) {
      return null
    }

    const output = []

    for (const row of node.getChildren()) {
      const rowOutput = []
      let isHeaderRow = false

      if ($isTableRowNode(row)) {
        for (const cell of row.getChildren()) {
          if (cell.hasHeader()) {
            isHeaderRow = true
          }
          if ($isElementNode(cell)) {
            rowOutput.push(exportChildren(cell))
          }
        }
      }

      output.push(`| ${rowOutput.join(" | ")} |`)

      if (isHeaderRow) {
        output.push(`| ${rowOutput.map(() => "---").join(" | ")} |`)
      }
    }

    return output.join("\n")
  },
  regExp: TABLE_ROW_REG_EXP,
  replace: (parentNode, _1, match) => {
    const matchCells = mapToTableCells(match[0])

    if (matchCells == null) {
      return
    }

    const rows = [matchCells]
    let sibling = parentNode.getPreviousSibling()
    let maxCells = matchCells.length

    while (sibling) {
      if (!$isParagraphNode(sibling)) {
        break
      }

      if (sibling.getChildrenSize() !== 1) {
        break
      }

      const firstChild = sibling.getFirstChild()

      if (!$isTextNode(firstChild)) {
        break
      }

      const cells = mapToTableCells(firstChild.getTextContent())

      if (cells == null) {
        break
      }

      maxCells = Math.max(maxCells, cells.length)
      rows.unshift(cells)
      const previousSibling = sibling.getPreviousSibling()
      sibling.remove()
      sibling = previousSibling
    }

    const table = $createTableNode()

    let hasHeader = false
    for (const cells of rows) {
      const isHeaderRow = cells[0].getTextContent() === "---"
      if (isHeaderRow) {
        hasHeader = true
      } else {
        const tableRow = $createTableRowNode()
        table.append(tableRow)

        for (let i = 0; i < maxCells; i++) {
          tableRow.append(
            i < cells.length ? cells[i].toggleHeaderStyle(0) : createTableCell(null),
          )
        }
      }
    }

    // Set header style to first row cells if needed
    if (hasHeader && sibling) {
      const child = sibling.getFirstChild()
      if ($isTableRowNode(child)) {
        const cells = child.getChildren()
        for (const element of cells) {
          $isTableCellNode(element) &&
            element.toggleHeaderStyle(TableCellHeaderStates.ROW)
        }
      }
    }

    const previousSibling = parentNode.getPreviousSibling()
    if (
      $isTableNode(previousSibling) &&
      getTableColumnsSize(previousSibling) === maxCells
    ) {
      previousSibling.append(...table.getChildren())
      parentNode.remove()
    } else {
      parentNode.replace(table)
    }

    table.selectEnd()
  },
  type: "element",
}

function getTableColumnsSize(table: TableNode) {
  const row = table.getFirstChild()
  return $isTableRowNode(row) ? row.getChildrenSize() : 0
}

const createTableCell = (textContent: string | null | undefined): TableCellNode => {
  const cell = $createTableCellNode(TableCellHeaderStates.NO_STATUS)
  const paragraph = $createParagraphNode()

  if (textContent != null) {
    paragraph.append($createTextNode(textContent.trim()))
  }

  cell.append(paragraph)
  return cell
}

const mapToTableCells = (textContent: string): TableCellNode[] | null => {
  const match = textContent.match(TABLE_ROW_REG_EXP)

  if (!match || !match[1]) {
    return null
  }

  return match[1].split("|").map((text) => createTableCell(text))
}

export const CUSTOM_TRANSFORMERS: Transformer[] = [
  // Element transformers
  TABLE,
  CHECK_LIST,
  UNORDERED_LIST,
  HEADING,
  ORDERED_LIST,
  QUOTE,

  // Text format transformers
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  STRIKETHROUGH,

  ...TEXT_MATCH_TRANSFORMERS,
]
